import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import FoundingScarcityStrip from "@/components/site/FoundingScarcityStrip";

const HERO_IMG =
  "https://images.unsplash.com/photo-1631308491952-040f80133535?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxtYXRjaGElMjBsYXR0ZXxlbnwwfHx8fDE3ODI2OTg4ODl8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({ onJoin, stats }) {
  const foundersClosed = stats?.founders_closed;
  const allClosed = stats?.all_closed;
  const ctaLabel = allClosed
    ? "Founding 100 closed"
    : foundersClosed
    ? "Grab a Matcha Pass"
    : "Become a Founding Member";
  return (
    <section id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-32 px-6 md:px-10 overflow-hidden grain">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-7"
          data-testid="hero-text"
        >
          <p className="overline text-[#5C7365] mb-6" data-testid="hero-overline">
            本当 · Hontō · Dehradun · {stats?.founders_closed && stats?.passes_closed ? "Founding closed" : "Founding 100"}
          </p>
          <h1 className="font-serif font-light text-[2.6rem] sm:text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.04] tracking-tight text-[#1A2B22] break-words">
            A bowl of <em className="text-[#2C4C3B]">stillness</em>,
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            stone-ground at <em className="text-[#C47253]">dawn</em>.
          </h1>
          <p className="mt-7 max-w-xl text-base md:text-lg text-[#5C7365] leading-relaxed">
            <span className="text-[#1A2B22]">Hontō (本当)</span> is Japanese for <span className="italic">true</span>.
            A slow-bar in Dehradun where the grade is named, the region is specific, and the price reflects what
            it actually costs to bring in. The first 100 members get their first cup of Shizuoka-grade matcha
            on us — no fee, no catch.
          </p>

          <FoundingScarcityStrip stats={stats} />

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Button
              onClick={onJoin}
              data-testid="hero-join-btn"
              className="rounded-full bg-primary text-primary-foreground hover:bg-[#1F382B] px-7 sm:px-8 h-12 text-[11px] sm:text-xs tracking-[0.2em] uppercase group"
            >
              Become a Founding Member
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <a
              href="#pass"
              data-testid="hero-pass-link"
              className="text-sm text-[#2C4C3B] underline underline-offset-4 decoration-[#C47253]/60 hover:decoration-[#C47253]"
            >
              See the Matcha Pass
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
            {[
              [`${stats?.founders_claimed ?? 0}/${stats?.founders_total ?? 45}`, "free cups claimed"],
              [`${stats?.passes_claimed ?? 0}/${stats?.passes_total ?? 55}`, "passes claimed"],
              [`${stats?.total_claimed ?? 0}/100`, "of the founding 100"],
            ].map(([n, l]) => (
              <div key={l} data-testid={`hero-stat-${l.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#2C4C3B] tabular-nums">{n}</div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#5C7365] mt-1 leading-tight">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5 relative"
        >
          <div className="aspect-[4/5] rounded-[28px] overflow-hidden border border-border bg-[#1A1A1A] relative">
            <img
              src={HERO_IMG}
              alt="Matcha being poured"
              className="w-full h-full object-cover opacity-90 hover:scale-105 transition-transform duration-[1200ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
            <img
              src={LOGO_URL}
              alt="Hontō mark"
              className="absolute bottom-4 left-4 w-14 h-14 opacity-90"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden md:block bg-background border border-border rounded-2xl px-5 py-4 shadow-sm">
            <p className="overline text-[#C47253]">In the cup</p>
            <p className="font-serif text-lg text-[#1A2B22] leading-tight">Two grades. One promise.</p>
            <p className="text-xs text-[#5C7365]">Never substituted to protect a margin.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
